"use client";

import {
    Carousel,
    CarouselApi,
    CarouselContent,
    CarouselNext,
    CarouselPrevious,
} from "@/components/shadcn/carousel";
import { cn } from "cn";
import Autoplay from "embla-carousel-autoplay";
import { ReactNode, useEffect, useState } from "react";

export function EventCarouselSection({
    title,
    description,
    emptyMessage,
    isEmpty,
    className,
    children,
}: {
    title: string;
    description: string;
    emptyMessage: string;
    isEmpty: boolean;
    className?: string;
    children: ReactNode;
}) {
    const [api, setApi] = useState<CarouselApi>();
    const [snapCount, setSnapCount] = useState(0);
    const [selectedIndex, setSelectedIndex] = useState(0);

    useEffect(() => {
        if (!api) return;

        const onSelect = () => setSelectedIndex(api.selectedScrollSnap());
        const onReInit = () => {
            setSnapCount(api.scrollSnapList().length);
            onSelect();
        };

        onReInit();
        api.on("select", onSelect);
        api.on("reInit", onReInit);

        return () => {
            api.off("select", onSelect);
            api.off("reInit", onReInit);
        };
    }, [api]);

    return (
        <section
            className={cn(
                "flex flex-col gap-6 rounded-2xl p-4 sm:p-6 lg:flex-row lg:items-center lg:gap-8 lg:px-12 lg:py-8",
                className,
            )}
        >
            <div className="flex flex-col gap-3 lg:w-56 lg:shrink-0">
                <h2 className="text-2xl font-medium tracking-tight">{title}</h2>
                <p className="text-muted-foreground">{description}</p>
            </div>

            {isEmpty ? (
                <p className="text-muted-foreground flex-1 py-8 text-center">
                    {emptyMessage}
                </p>
            ) : (
                <div className="flex min-w-0 flex-1 flex-col gap-4">
                    <Carousel
                        setApi={setApi}
                        opts={{ align: "start", loop: true }}
                        plugins={[
                            Autoplay({
                                delay: 6000,
                                stopOnMouseEnter: true,
                                stopOnInteraction: true,
                            }),
                        ]}
                        className="w-full"
                    >
                        <CarouselContent>{children}</CarouselContent>
                        <CarouselPrevious
                            variant="default"
                            className="-left-3 rounded-lg lg:-left-8"
                        />
                        <CarouselNext
                            variant="default"
                            className="-right-3 rounded-lg lg:-right-8"
                        />
                    </Carousel>

                    {snapCount > 1 && (
                        <div className="flex items-center justify-center gap-2.5">
                            {Array.from({ length: snapCount }).map(
                                (_, index) => (
                                    <button
                                        key={index}
                                        type="button"
                                        aria-label={`${index + 1} / ${snapCount}`}
                                        aria-current={index === selectedIndex}
                                        onClick={() => api?.scrollTo(index)}
                                        className={cn(
                                            "size-2 rounded-full transition-colors",
                                            index === selectedIndex
                                                ? "bg-primary"
                                                : "bg-muted-foreground/30",
                                        )}
                                    />
                                ),
                            )}
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}
